import { useAct } from 'FlowBuilder/store'
import TextField from 'Shared/components/TextField'
import CheckboxField from 'Shared/components/CheckboxField'
import SelectField from 'Shared/components/SelectField'
import AsyncSelect from 'Shared/components/AsyncSelect'
import useFlowTranslate from 'FlowBuilder/hooks/useFlowTranslate'
import OrganisationField from 'Shared/components/OrganisationField'

export default function PageSettingsForm() {
  const act = useAct()
  const [templateFieldErrors, setTemplateFieldErrors] = useState({})
  const [showGoalsWarning, setShowGoalsWarning] = useState(false)

  const { id, slug, effortId, settings: { assignNewTargetsToOrganisationId } } = useSel(s => s.builder?.flow || {})
  const errors = useSel(s => s.local.errors?.flow || {})
  const isEditingTemplate = useSel((s) => s.local.isEditingTemplate)

  const templateFields = useSel(s =>
    (s.builder.flowTemplate?.fields || []).filter(f => f.setup)
  )

  const templateFieldValues = useSel(s => s.builder.flow.templateFields)

  const {
    translated: { buttonTextSubmit, buttonTextNext },
    fallback: { buttonTextSubmit: buttonTextSubmitFallback, buttonTextNext: buttonTextNextFallback },
    textContentUpdater,
    flag
  } = useFlowTranslate()

  const createTemplateFieldHandler = (field) => (value) => {
    setTemplateFieldErrors(prev => ({
      ...prev,
      [field.slug]: null
    }))

    if (field.numberOnly && value && !/^\d*$/.test(value)) {
      setTemplateFieldErrors(prev => ({
        ...prev,
        [field.slug]: 'Please enter numbers only'
      }))
      return
    }

    act.builder.setTemplateField({ slug: field.slug, value })
  }

  function updateEffortId(value) {
    act.builder.updateFlow('effortId', value)
    act.answers.setAllSetsGoalIdsNull() // Reset all answers' setsGoalId when effort changes
  }

  function updateAssignNewTargetsToOrganisationId(value) {
    act.builder.updateFlowSetting('assignNewTargetsToOrganisationId', value)
  }

  function onClickGoal() {
    if (effortId) {
      setShowGoalsWarning(true)
    }
  }

  return (
    <form>
      {!isEditingTemplate && <TextField
        label="Page URL slug"
        value={slug}
        onChange={(value) => { act.builder.updateFlow('slug', value) }}
        error={errors.slug}
      /> }

      <div className='field' onClick={onClickGoal}>
        <label>Goal</label>
        { showGoalsWarning && <p className='error'>Changing the goal for this page will remove any goals connected to answers.</p> }
        {!isEditingTemplate && <AsyncSelect
          value={effortId}
          label="Effort"
          entity='effort'
          onChange={updateEffortId}
          error={errors.slug}
        /> }
        <small>Goals optionally let you organise campaigns into long-running organisational initiatives. For example, a survey might be part of an 'Indicative strike ballot' goal.</small>
      </div>

      <OrganisationField value={assignNewTargetsToOrganisationId} 
        allowBlank
        showClearButton
        nullShowClientNationalName
        setFieldValue={(_field, id, isNationalOrg) => updateAssignNewTargetsToOrganisationId(isNationalOrg ?  null : id)}
        label="Assign new joiners from this page to an organisation"
        />

      <TextField
        label={`Submit button text ${flag}`}
        value={buttonTextSubmit ?? ''}
        placeholder={buttonTextSubmitFallback}
        onChange={(val) => textContentUpdater('buttonTextSubmit', val)}
      />
      <TextField
        label={`Next button text ${flag}`}
        value={buttonTextNext ?? ''}
        placeholder={buttonTextNextFallback}
        onChange={(val) => textContentUpdater('buttonTextNext', val)}
      />

      {templateFields.map((field) => {
        const value = templateFieldValues[field.slug]
        const handleChange = createTemplateFieldHandler(field)
        const fieldError = templateFieldErrors[field.slug]

        if (field.questionType === 'text')
          return <TextField key={field.slug} label={field.label} value={value} onChange={handleChange} error={fieldError} />
        if (field.questionType === 'checkbox')
          return <CheckboxField key={field.slug} label={field.label} value={value} onChange={handleChange} />
        if (field.questionType === 'select')
          return <SelectField key={field.slug} label={field.label} value={value} onChange={handleChange} options={field.options} />
        if (field.questionType === 'async_select')
          return <div className='field'>
            <label>{field.label}</label>
            <AsyncSelect key={field.slug} value={value} onChange={handleChange} entity={field.asyncEntity} />
          </div>
        return null
      })}
    </form>
  )
}
